import { createFileRoute, Link } from "@tanstack/react-router";
import { Sparkles, Home, KeyRound, Building2, Shield, Star, Clock, MapPin, ArrowRight, CheckCircle2, Facebook, Instagram } from "lucide-react";
import { SITE } from "@/config/site";
import { CTAButtons } from "@/components/site/CTAButtons";
import { GoogleReviews } from "@/components/site/GoogleReviews";
import { FAQ } from "@/components/site/FAQ";
import { GuidePrices } from "@/components/site/GuidePrices";
import { TrustedLocal } from "@/components/site/TrustedLocal";
import hero from "@/assets/hero-clean-living-room.webp";
import suppliesAssetUrl from "@/assets/cleaning-supplies.webp";
const suppliesAsset = { url: suppliesAssetUrl };
import heroCleanerAssetUrl from "@/assets/izhub-hero-cleaner.webp";
const heroCleanerAsset = { url: heroCleanerAssetUrl };


const SITE_URL = "https://izhubcleaning.uk";
const AREAS = ["Tidworth", "Bulford", "Larkhill", "Amesbury", "Ludgershall", "Andover", "Salisbury", "Salisbury Plain"];

const homeJsonLd = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": ["LocalBusiness", "HomeAndConstructionBusiness"],
      "@id": `${SITE_URL}/#localbusiness`,
      name: "IZHUB Cleaning Service",
      alternateName: "IZHUB Cleaning",
      description:
        "Professional domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning across Tidworth, Andover, Salisbury and Salisbury Plain. Fully insured, inspection-ready.",
      url: SITE_URL,
      image: `${SITE_URL}/og-image.jpg`,
      logo: `${SITE_URL}/og-image.jpg`,
      telephone: ["+44 7763 933920"],
      email: "info@izhubcleaning.uk",
      priceRange: "££",
      currenciesAccepted: "GBP",
      paymentAccepted: "Cash, Bank transfer, Card",
      address: {
        "@type": "PostalAddress",
        streetAddress: "Talavera Way",
        addressLocality: "Tidworth",
        addressRegion: "Wiltshire",
        postalCode: "SP9 7WH",
        addressCountry: "GB",
      },
      geo: {
        "@type": "GeoCoordinates",
        latitude: 51.160456,
        longitude: -1.6642579,
      },
      areaServed: AREAS.map((name) => ({ "@type": "City", name })),
      serviceArea: {
        "@type": "GeoCircle",
        geoMidpoint: { "@type": "GeoCoordinates", latitude: 51.160456, longitude: -1.6642579 },
        geoRadius: 30000,
      },
      openingHoursSpecification: [
        {
          "@type": "OpeningHoursSpecification",
          dayOfWeek: [
            "Monday",
            "Tuesday",
            "Wednesday",
            "Thursday",
            "Friday",
            "Saturday",
          ],
          opens: "08:00",
          closes: "18:00",
        },
      ],
      sameAs: [
        "https://www.google.com/maps/place/IZHUB+Cleaning+Service/@51.160456,-1.6642579,15z/data=!4m6!3m5!1s0xaa0d05ebd417799d:0x4b6d9fef3026d0a4!8m2!3d51.160456!4d-1.6642579!16s%2Fg%2F11z308wqpq",
      ],
      aggregateRating: {
        "@type": "AggregateRating",
        ratingValue: "5.0",
        reviewCount: "11",
        bestRating: "5",
        worstRating: "1",
      },
      hasOfferCatalog: {
        "@type": "OfferCatalog",
        name: "Cleaning Services",
        itemListElement: [
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Domestic Cleaning" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Deep Cleaning" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "End of Tenancy Cleaning" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Military March-Out Cleaning" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Office & Commercial Cleaning" } },
          { "@type": "Offer", itemOffered: { "@type": "Service", name: "Airbnb & Short-Let Cleaning" } },
        ],
      },
    },
    ...[
      {
        name: "Domestic Cleaning",
        description:
          "Regular weekly, fortnightly or one-off house cleaning across Tidworth, Andover and Salisbury. Fully insured with professional-grade products included.",
        price: "22",
        priceType: "per hour",
      },
      {
        name: "End of Tenancy Cleaning",
        description:
          "Deposit-back end of tenancy cleaning built around letting-agent inventory checklists. Ovens, limescale, cupboards, skirting, extractor fans — inspection-ready.",
        price: "160",
        priceType: "from",
      },
      {
        name: "Military March-Out Cleaning",
        description:
          "Specialist march-out cleans for MOD service family accommodation across Salisbury Plain. Inspection-standard results, pass first time, re-clean guarantee.",
        price: "0",
        priceType: "quote",
      },
      {
        name: "Deep Cleaning",
        description:
          "Top-to-bottom deep cleans for kitchens, bathrooms and full homes. Ideal for move-ins, spring cleans and post-renovation clear-ups.",
        price: "0",
        priceType: "quote",
      },
      {
        name: "Office & Commercial Cleaning",
        description:
          "Reliable contract cleaning for small offices and workspaces across Wiltshire. Daily, weekly or bespoke schedules.",
        price: "0",
        priceType: "quote",
      },
      {
        name: "Airbnb & Short-Let Cleaning",
        description:
          "Fast, consistent turnaround cleans between guests for Airbnb hosts and short-let landlords. Linen change on request.",
        price: "0",
        priceType: "quote",
      },
    ].map((s) => {
      const base: Record<string, unknown> = {
        "@type": "Service",
        "@id": `${SITE_URL}/#service-${s.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`,
        serviceType: s.name,
        name: s.name,
        description: s.description,
        provider: { "@id": `${SITE_URL}/#localbusiness` },
        areaServed: AREAS.map((name) => ({ "@type": "City", name })),
      };
      if (s.price !== "0") {
        base.offers = {
          "@type": "Offer",
          price: s.price,
          priceCurrency: "GBP",
          availability: "https://schema.org/InStock",
          priceSpecification: {
            "@type": "PriceSpecification",
            price: s.price,
            priceCurrency: "GBP",
            description: s.priceType,
          },
        };
      }
      return base;
    }),
  ],
};

export const Route = createFileRoute("/")({
  head: () => ({
    meta: [
      { title: "Domestic & Commercial Cleaning Services in Tidworth | IZHUB Cleaning" },
      { name: "description", content: "Professional cleaning services in Tidworth from £22 per hour. Domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning across Tidworth, Perham Down, Ludgershall, Andover and Salisbury." },
      { property: "og:title", content: "Domestic & Commercial Cleaning Services in Tidworth | IZHUB Cleaning" },
      { property: "og:description", content: "Trusted, insured cleaners in Tidworth. Domestic, deep, end of tenancy, Airbnb, office and military march-out cleaning — guide prices from £22 per hour." },
      { property: "og:url", content: "https://izhubcleaning.uk/" },
      { property: "og:image", content: "https://izhubcleaning.uk/og-image.jpg" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "IZHUB Cleaning Service — Trusted local cleaners across Salisbury Plain" },
      { name: "twitter:image", content: "https://izhubcleaning.uk/og-image.jpg" },
    ],
    links: [{ rel: "canonical", href: "https://izhubcleaning.uk/" }],
    scripts: [
      {
        type: "application/ld+json",
        children: JSON.stringify(homeJsonLd),
      },
    ],
  }),
  component: Home_,
});

const services = [
  { icon: Home, title: "Domestic cleaning", desc: "Regular weekly or fortnightly cleans for busy homes.", hash: "domestic" },
  { icon: Sparkles, title: "Deep cleaning", desc: "Thorough, top-to-bottom cleans for kitchens, bathrooms and more.", hash: "deep" },
  { icon: KeyRound, title: "End of tenancy", desc: "Inspection-ready cleans for tenants, landlords and agents.", hash: "tenancy" },
  { icon: Building2, title: "Office cleaning", desc: "Reliable cleaning for small offices and workspaces.", hash: "office" },
  { icon: Shield, title: "Military march-out", desc: "Specialist cleaning for MOD accommodation inspections.", hash: "military" },
  { icon: Star, title: "Airbnb & short-lets", desc: "Fast, consistent turnarounds between guests.", hash: "airbnb" },
];

const areas = ["Tidworth", "Bulford", "Larkhill", "Amesbury", "Ludgershall", "Andover", "Salisbury", "Salisbury Plain"];

function Home_() {
  return (
    <>
      {/* Hero */}
      <section className="relative overflow-hidden bg-cream/85 backdrop-blur-sm">
        <img
          src={heroCleanerAsset.url}
          alt=""
          aria-hidden="true"
          width={1600}
          height={1200}
          fetchPriority="high"
          decoding="async"
          className="pointer-events-none select-none absolute inset-y-0 right-0 h-full w-full md:w-3/4 lg:w-3/5 object-cover object-top opacity-15 md:opacity-20 [mask-image:linear-gradient(to_left,black,transparent)]"
        />
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-r from-cream via-cream/80 to-transparent" />
        <div className="container-x relative py-16 md:py-24 lg:py-28">
          <div className="mx-auto max-w-3xl text-center">
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-[1.02]">
              Professional Cleaning Services in <span className="rainbow-text">Tidworth</span>
            </h1>
            <p className="mt-6 text-lg text-muted-foreground max-w-2xl mx-auto">
              Domestic and commercial cleaning across Tidworth, Perham Down, Ludgershall, Andover and Salisbury — deep cleans, end of tenancy, Airbnb turnarounds and military march-outs. Fully insured, inspection-ready, from £22 per hour.
            </p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
            <div className="mt-10 grid grid-cols-3 gap-4 max-w-lg mx-auto">
              <TrustStat value="2 hr" label="Quote reply" />
              <TrustStat value="5★" label="Service standard" />
              <TrustStat value="Fully" label="Insured" />
            </div>
          </div>
        </div>
      </section>

      {/* Business summary (plain-language for search & AI engines) */}
      <section className="border-b border-border bg-background/85 backdrop-blur-sm">
        <div className="container-x py-8 md:py-10">
          <p className="max-w-4xl mx-auto text-center text-base md:text-lg text-foreground/85 leading-relaxed">
            <strong className="text-foreground">IZHUB Cleaning Services</strong> is a local cleaning company providing domestic cleaning, deep cleaning, end of tenancy cleaning, Airbnb and short-let cleaning, office cleaning and military march-out cleaning across Tidworth, Bulford, Larkhill, Amesbury, Ludgershall, Andover, Salisbury, Salisbury Plain and surrounding areas. Call <a href="tel:+447763933920" className="text-primary underline underline-offset-4">07763 933 920</a>, or email <a href="mailto:info@izhubcleaning.uk" className="text-primary underline underline-offset-4">info@izhubcleaning.uk</a> for a free quote — usual reply within 2 hours.
          </p>
        </div>
      </section>

      {/* Who we help ribbon */}
      <section className="border-y border-border bg-background/85 backdrop-blur-sm overflow-hidden">
        <div className="container-x py-8 md:py-10">
          <p className="eyebrow justify-center flex">Trusted cleaning support for</p>
          <div className="marquee mt-5" aria-label="Who we help">
            <ul className="marquee-track list-none m-0 p-0" aria-hidden="false">
              {[...Array(2)].flatMap((_, dup) =>
                ["Homeowners", "Tenants & Landlords", "Offices & Businesses", "Military Families", "Airbnb & Short-lets"].map((label) => (
                  <li
                    key={`${dup}-${label}`}
                    aria-hidden={dup === 1 ? "true" : undefined}
                    className="shrink-0 rounded-full border border-border bg-cream px-5 py-2 text-sm text-foreground/80"
                  >
                    {label}
                  </li>
                ))
              )}
            </ul>
          </div>
        </div>
      </section>


      {/* Services */}
      <section className="py-20 md:py-28">
        <div className="container-x">
          <div className="grid gap-10 lg:grid-cols-2 lg:items-center">
            <div className="text-surface max-w-2xl">
              <span className="eyebrow">What we do</span>
              <h2 className="mt-4 font-display text-3xl md:text-5xl">Cleaning services for <span className="rainbow-text">every situation</span>.</h2>
              <p className="mt-4 text-muted-foreground">From weekly household cleans to inspection-critical march-outs — one trusted team, one call.</p>
            </div>
            <img src={hero} alt="Bright, spotlessly cleaned living room with soft natural light" width={1600} height={1200} loading="lazy" className="rounded-3xl shadow-lift w-full h-auto object-cover aspect-[4/3]" />
          </div>
          <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {services.map((s) => (
              <Link key={s.title} to="/services" hash={s.hash} className="group rounded-3xl border border-border bg-card p-6 transition-all hover:border-primary hover:-translate-y-0.5 hover:shadow-lift">
                <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sage-soft text-primary"><s.icon className="h-5 w-5" /></span>
                <h3 className="mt-5 font-display text-xl">{s.title}</h3>
                <p className="mt-2 text-sm text-muted-foreground">{s.desc}</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-sm text-primary font-medium">Learn more <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" /></span>
              </Link>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap gap-3">
            <Link to="/domestic-cleaning-tidworth" className="btn-outline">Domestic house cleaning in Tidworth</Link>
            <Link to="/office-cleaning-tidworth" className="btn-outline">Office &amp; commercial cleaning in Tidworth</Link>
          </div>
        </div>
      </section>

      {/* Guide prices */}
      <GuidePrices />



      {/* How it works */}
      <section className="bg-cream/85 backdrop-blur-sm py-20 md:py-28">
        <div className="container-x grid gap-14 lg:grid-cols-[1fr_1.1fr] lg:items-center">
          <img src={suppliesAsset.url} alt="IZHUB cleaner holding professional cleaning supplies" width={993} height={1584} loading="lazy" className="rounded-3xl w-full h-auto max-h-[720px] object-contain shadow-soft" />
          <div>
            <span className="eyebrow">How it works</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl">Simple from start to <span className="rainbow-text">spotless</span>.</h2>
            <p className="mt-4 text-muted-foreground">No complicated booking. No hidden fees. Just reliable cleaning in three straightforward steps.</p>
            <ol className="mt-8 space-y-6">
              <Step n="01" title="Tell us about your property" text="Share your details and preferred dates. No lengthy forms — just the essentials." />
              <Step n="02" title="Get a clear price within 2 hours" text="Transparent, no-obligation quote. The price you see is the price you pay." />
              <Step n="03" title="We arrive on time. You enjoy the results" text="Professional equipment, thorough approach, consistent standards every visit." />
            </ol>
            <div className="mt-8"><Link to="/quote" className="btn-primary">Start now — takes 2 minutes <ArrowRight className="h-4 w-4" /></Link></div>
          </div>
        </div>
      </section>

      {/* Trust section */}
      <section className="py-20 md:py-28">
        <div className="container-x">
          <div className="text-surface max-w-2xl">
            <span className="eyebrow">Why IZHUB</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl">Cleaning you can <span className="rainbow-text">trust in your home</span>.</h2>
            <p className="mt-4 text-muted-foreground">We understand what it takes to earn trust when someone lets a cleaner into their home or property. Here's how we do it.</p>
          </div>
          <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
            <TrustCard icon={Shield} title="Fully insured" text="Public liability cover for every visit — for your peace of mind." />
            <TrustCard icon={CheckCircle2} title="Attention to detail" text="We clean the spots most cleaners miss — skirting boards, edges, cupboard interiors." />
            <TrustCard icon={Clock} title="Reliable & punctual" text="On time, every time. Consistent standards on every visit." />
            <TrustCard icon={Star} title="Friendly & flexible" text="Clear communication and flexible scheduling around your life." />
          </div>
        </div>
      </section>

      {/* Trusted locally */}
      <TrustedLocal />

      {/* Google Reviews */}
      <GoogleReviews />

      {/* Social media */}
      <section className="py-16 md:py-20 bg-cream/85 backdrop-blur-sm">
        <div className="container-x">
          <div className="max-w-2xl mx-auto text-center">
            <span className="eyebrow justify-center flex">Social media</span>
            <h2 className="mt-4 font-display text-3xl md:text-4xl">See our latest <span className="rainbow-text">cleans</span></h2>
            <p className="mt-4 text-muted-foreground">Follow IZHUB Cleaning Services on Facebook and Instagram for cleaning updates, recent work and available slots.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <a
                href={SITE.facebook}
                target="_blank"
                rel="noreferrer"
                aria-label="Visit IZHUB Cleaning Services on Facebook"
                className="inline-flex items-center gap-2 btn-outline"
              >
                <Facebook className="h-4 w-4" /> Follow us on Facebook
              </a>
              <a
                href={SITE.instagram}
                target="_blank"
                rel="noreferrer"
                aria-label="Visit IZHUB Cleaning Services on Instagram"
                className="inline-flex items-center gap-2 btn-outline"
              >
                <Instagram className="h-4 w-4" /> Follow us on Instagram
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* March-out specialist */}
      <section className="bg-primary text-primary-foreground py-20 md:py-28">
        <div className="container-x grid gap-10 lg:grid-cols-[1.1fr_1fr] lg:items-center">
          <div>
            <span className="text-xs font-medium tracking-[0.14em] uppercase text-sage-soft">What makes us different</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl text-primary-foreground">March-out cleaning <span className="text-sage-soft">specialists</span>.</h2>
            <p className="mt-6 text-primary-foreground/85 max-w-xl">
              We understand military accommodation inspections inside and out. Our team knows exactly what inspectors look for and cleans to that standard — so you pass first time without the stress.
            </p>
            <p className="mt-4 text-primary-foreground/70 max-w-xl">
              From cupboard interiors to commonly-inspected areas, we cover every detail. Trusted by service families across Tidworth, Bulford and Larkhill.
            </p>
            <Link to="/services" hash="military" className="mt-8 inline-flex items-center gap-2 rounded-full bg-background text-foreground px-6 py-3 font-medium hover:bg-sage-soft transition-colors">Learn about military cleaning <ArrowRight className="h-4 w-4" /></Link>
          </div>
          <ul className="grid grid-cols-2 gap-3">
            {["Oven & hob detail","Cupboard interiors","Skirting & edges","Windows & sills","Bathroom descale","Carpet vacuum","Wall spot-clean","Bin & drain areas"].map(x => (
              <li key={x} className="rounded-2xl border border-primary-foreground/15 bg-primary-foreground/5 px-4 py-3 text-sm flex items-center gap-2"><CheckCircle2 className="h-4 w-4 text-sage-soft shrink-0" />{x}</li>
            ))}
          </ul>
        </div>
      </section>

      {/* Service areas */}
      <section className="py-20 md:py-28">
        <div className="container-x">
          <div className="text-surface max-w-2xl">
            <span className="eyebrow">Service areas</span>
            <h2 className="mt-4 font-display text-3xl md:text-5xl">Cleaners across <span className="rainbow-text">Salisbury Plain</span>.</h2>
            <p className="mt-4 text-muted-foreground">We cover Tidworth, Bulford, Larkhill, Amesbury, Ludgershall, Andover, Salisbury and the wider Salisbury Plain area.</p>
          </div>
          <div className="mt-10 flex flex-wrap gap-2.5">
            {areas.map((a) => (
              <span key={a} className="inline-flex items-center gap-2 rounded-full border border-border bg-card px-4 py-2 text-sm"><MapPin className="h-3.5 w-3.5 text-primary" />{a}</span>
            ))}
          </div>
          <div className="text-surface mt-6 w-fit">
            <p className="text-sm text-muted-foreground">Not sure if we cover your area? <Link to="/contact" className="text-primary underline underline-offset-4">Just ask</Link>.</p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <FAQ />

      {/* Final CTA */}
      <section className="pb-24">
        <div className="container-x">
          <div className="rounded-3xl bg-sage-soft/60 border border-sage/30 p-8 md:p-14 text-center">
            <h2 className="font-display text-3xl md:text-5xl max-w-2xl mx-auto">Ready for a home that feels <span className="text-primary">brand new</span>?</h2>
            <p className="mt-4 text-muted-foreground max-w-xl mx-auto">Get your free, no-obligation quote and we'll respond within 2 hours during business hours.</p>
            <div className="mt-8 flex justify-center"><CTAButtons /></div>
            <p className="mt-6 text-xs text-muted-foreground">Fully insured · No hidden fees · Satisfaction guaranteed</p>
          </div>
        </div>
      </section>
    </>
  );
}

function Dot() { return <span className="h-1 w-1 rounded-full bg-border" />; }

function TrustStat({ value, label }: { value: string; label: string }) {
  return (
    <div className="rounded-2xl border border-border bg-card px-4 py-3">
      <div className="font-display text-2xl text-primary">{value}</div>
      <div className="text-xs text-muted-foreground mt-0.5">{label}</div>
    </div>
  );
}

function Step({ n, title, text }: { n: string; title: string; text: string }) {
  return (
    <li className="flex gap-5">
      <span className="font-display text-2xl rainbow-text shrink-0 w-12">{n}</span>
      <div>
        <h3 className="font-display text-xl">{title}</h3>
        <p className="mt-1 text-sm text-muted-foreground">{text}</p>
      </div>
    </li>
  );
}

function TrustCard({ icon: Icon, title, text }: { icon: typeof Shield; title: string; text: string }) {
  return (
    <div className="rounded-3xl border border-border bg-card p-6">
      <span className="grid h-11 w-11 place-items-center rounded-2xl bg-sage-soft text-primary"><Icon className="h-5 w-5" /></span>
      <h3 className="mt-5 font-display text-lg">{title}</h3>
      <p className="mt-2 text-sm text-muted-foreground">{text}</p>
    </div>
  );
}
